import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'src/store/store';

import { fetchProgram, fetchModules, fetchCurrentModule } from 'src/modules/programs/actions';
import { showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { getProgramsAboutSeo, getProgramsModuleSeo } from 'src/utils/seo';

import { DefaultLayout } from 'src/components/layouts';
import ErrorPage from 'src/components/error';
import Header from 'src/modules/programs/components/header';
import Modules from 'src/modules/programs/components/modules';
import MainContent from 'src/modules/programs/components/mainContent';
import { PageContext } from 'src/constants/types';

type Props = {
  moduleCode?: string;
  slug: string;
}
export default function ProgramsPage({ moduleCode, slug }: Props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(({ context }) => context.currentUser);
  const { currentModule, details = {}, isInvalidModule, progress } = useSelector(({ pages }) => pages.programs);

  const { code: programCode, name: programName, description } = details;
  const isInvalidProgram = !programCode;
  const latestCurrentUser = useRef(currentUser); // Track latest value of currentUser
  const currentProgress = useRef(progress);
  const bodyRef = useRef(null);

  // Fetch modules after sign up success (we refresh the page after login success)
  useEffect(() => {
    // 2 endpoints populate currentUser in the store after sign up success, we only re-fetch
    // modules once by checking that currentUser changes from null (visitor state) to an object
    if (!latestCurrentUser.current && !!currentUser) {
      dispatch(fetchModules(slug));
      latestCurrentUser.current = currentUser;
    }
  }, [currentUser, dispatch, slug]);

  useEffect(() => {
    if (currentProgress.current !== 100 && progress === 100) {
      trackFootfallEvent(Event.Clicked, {
        clickType: 'program',
        clickMetadata: { section: Section.CompletedProgram },
      });
      dispatch(showModal({ modalType: ModalType.ProgramCompleted }));
    }
  }, [progress, dispatch]);

  if (isInvalidProgram || isInvalidModule) {
    return (
      <DefaultLayout>
        <ErrorPage />
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout
      fullWidth={true}
      seo={
        moduleCode
          ? getProgramsModuleSeo(slug, programName, moduleCode, currentModule)
          : getProgramsAboutSeo(slug, programName, description)
      }
    >
      <Header />
      <Body ref={bodyRef}>
        <Modules bodyRef={bodyRef} />
        <MainContent />
      </Body>
    </DefaultLayout>
  );
}

ProgramsPage.getInitialProps = async ({ store: { dispatch, getState }, query: { slug, moduleCode } }: PageContext) => {
  const state = getState();
  const { details: { code }, currentModule } = state.pages.programs;
  const currentCode = currentModule ? currentModule.currentCode : null;

  if (slug !== code) {
    await Promise.all([
      dispatch(fetchProgram(slug)),
      dispatch(fetchModules(slug)),
    ]);
  }

  if (currentCode !== moduleCode) {
    await dispatch(fetchCurrentModule(moduleCode));
  }

  return {
    moduleCode,
    slug,
  };
};

const Body = styled.div`
  max-width: 1200px;
  margin: auto;
  padding: 30px 20px 80px;
  position: relative;
  display: flex;

  @media (${props => props.theme.underScreenMedium}) {
    padding: 0 0 80px;
    flex-direction: column;
  }

  @media (${props => props.theme.underScreenXsmall}) {
    padding-bottom: 30px;
  }
`;
